import { Bounds, LatLngBounds, Layer, Map, Point, Polyline } from 'leaflet';
import { MAX_OFFLINE_TILES } from '../../map-config';
import {
  downloadTile,
  getStorageInfo,
  saveTile,
  TileInfo,
  TileLayerOffline,
  truncate,
} from 'leaflet.offline';
import { TileStatus } from './map.component';

export const tileStatus: TileStatus = {
  tilesSaved: 0,
  tilesToSave: 0,
  done: false,
};

export function downloadTilesForOffline(
  zoomLevelsToSave: number[],
  map: Map,
  layer: TileLayerOffline,
  mapBounds: LatLngBounds,
  allSpans: Layer[],
) {
  getStorageInfo(layer._url).then((tileStorage) => {
    if (tileStorage.length > 6500) {
      truncate();
      tileStorage = [];
    }
    const StoredTiles: TileInfo[] = [];
    tileStorage.forEach((storedTile) => {
      const storedTileLatLng = tileCoordsToLatLgn(
        layer.getTileSize().x,
        new Point(storedTile.x, storedTile.y),
        storedTile.z,
        map,
      );
      if (storedTileLatLng.overlaps(mapBounds)) {
        StoredTiles.push(storedTile);
      }
    });
    const tiles: TileInfo[] = [];
    zoomLevelsToSave.forEach((zoom) => {
      layer
        .getTileUrls(latLngBoundToBound(map, mapBounds, zoom), zoom)
        .forEach((tile) => {
          if (!StoredTiles.find((tileInfo) => tile.key === tileInfo.key)) {
            const tileLatLngBounds = tileCoordsToLatLgn(
              layer.getTileSize().x,
              new Point(tile.x, tile.y),
              zoom,
              map,
            );
            if (isSpanInTile(allSpans, tileLatLngBounds)) {
              tiles.push(tile);
              tileStatus.tilesToSave += 1;
            }
          }
        });
    });
    const loader = async (): Promise<void> => {
      const tile = tiles.shift();
      setTileStatus();
      if (tile === undefined) {
        return Promise.resolve();
      }
      const blob: Blob = await downloadTile(tile.url);
      if (blob) {
        tileStatus.tilesSaved += 1;
        await saveTile(tile, blob);
      }
      return loader();
    };
    const parallel = Math.min(tiles.length, 50);
    for (let i = 0; i < parallel; i += 1) {
      loader();
    }
  });
}

function tileCoordsToLatLgn(
  tileSize: number,
  point: Point,
  zoom: number,
  map: Map,
) {
  const tileSizePoint = new Point(tileSize, tileSize);

  const nwPoint = point.scaleBy(tileSizePoint);
  const nePoint = nwPoint.add(new Point(tileSize, 0));
  const swPoint = nwPoint.add(new Point(0, tileSize));

  const ne = map.unproject(nePoint, zoom);
  const sw = map.unproject(swPoint, zoom);

  return new LatLngBounds(sw, ne);
}

function latLngBoundToBound(
  map: Map,
  bounds: LatLngBounds,
  zoom: number,
): Bounds {
  const topLeft = map.project(bounds.getNorthWest(), zoom);
  const bottomRight = map.project(bounds.getSouthEast(), zoom);

  return new Bounds(topLeft, bottomRight);
}

function isSpanInTile(allSpans: Layer[], tileBounds: LatLngBounds) {
  return allSpans?.find((span) =>
    tileBounds.intersects((span as Polyline).getBounds()),
  );
}
function setTileStatus() {
  if (tileStatus.tilesToSave === tileStatus.tilesSaved) {
    tileStatus.done = true;
  } else {
    tileStatus.done = false;
  }
}

export function estimateTileCount(
  map: Map,
  bounds: LatLngBounds,
  zoomLevels: number[],
  tileSize = 256,
): number {
  let total = 0;
  for (const zoom of zoomLevels) {
    const nw = map.project(bounds.getNorthWest(), zoom);
    const se = map.project(bounds.getSouthEast(), zoom);
    const tilesX = Math.ceil((se.x - nw.x) / tileSize);
    const tilesY = Math.ceil((se.y - nw.y) / tileSize);
    total += tilesX * tilesY;
  }
  return total;
}

export function shouldDownloadOfflineTiles(
  map: Map,
  bounds: LatLngBounds,
  zoomLevels: number[],
): boolean {
  const estimatedTiles = estimateTileCount(map, bounds, zoomLevels);
  return estimatedTiles < MAX_OFFLINE_TILES;
}
